import React from 'react';

import { DATA_TABLE_SIZE, DataTableColumn, DataTableContextProvider } from 'cpool-components/general/DataTable';
import { LOAN_REQUEST_OFFER_SOURCES } from 'cpool-constants';
import type { SchemaEuropaceOffer } from 'cpool-schemas';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';

import { AcceptAllOffersButton } from './AcceptAllOffersButton';
import { EuropaceOfferResultsRow } from './EuropaceOfferResultsRow';
import { useOffersByRequestId } from '~/features/leadDetails/hooks/useOffersByRequestId';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
    currentRequestId?: string;
};

const COLUMNS = [
    'BANK_LOGO',
    'DATUM',
    'KREDITBETRAG',
    'LAUFZEIT',
    'RATE',
    'ZINS',
    'RESIDUAL_DEBT_INSURANCE',
    'INFO',
    'OFFER_ACCEPTANCE',
] as const;

export const BankOfferResults: React.FC<Props> = ({ currentRequestId, lead }) => {
    const { t } = useTranslation();
    const { data: offerRequestArray } = useOffersByRequestId({ id: lead.id, requestId: currentRequestId });

    if (!offerRequestArray) return null;

    const allOffers = offerRequestArray.reduce((acc, curr) => {
        return [...acc, ...curr.offers];
    }, [] as SchemaEuropaceOffer[]);

    return (
        <div className="mt-2 flex flex-col gap-2">
            <div className="flex justify-end">
                <AcceptAllOffersButton currentRequestId={currentRequestId} lead={lead} />
            </div>
            <DataTableContextProvider size={DATA_TABLE_SIZE.SM}>
                <div className="overflow-x-auto rounded-md border border-grey-outline-light bg-white">
                    <table className="w-full table-auto">
                        <thead>
                            <tr>
                                {COLUMNS.map((column) => (
                                    <DataTableColumn
                                        key={column}
                                        field={column}
                                        align="center"
                                        name={t(`BANK_CREDIT_REQUEST.OFFERS_TABLE.${column}`)}
                                        className="whitespace-nowrap"
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {offerRequestArray.map((offersResponse) => {
                                switch (offersResponse.source) {
                                    case LOAN_REQUEST_OFFER_SOURCES.EUROPACE:
                                        return offersResponse?.offers.map((offer, index) => (
                                            <EuropaceOfferResultsRow
                                                key={offer.id}
                                                offer={offer}
                                                lead={lead}
                                                createdAt={offersResponse.createdAt}
                                                requestId={currentRequestId}
                                                isOdd={index % 2 === 1}
                                                source={offersResponse.source}
                                            />
                                        ));
                                    default:
                                        return null;
                                }
                            })}
                        </tbody>
                    </table>
                </div>
            </DataTableContextProvider>
            {allOffers.length === 0 && (
                <div className="text-center text-sm text-grey">{t('BANK_CREDIT_REQUEST.OFFERS_TABLE.NO_OFFERS')}</div>
            )}
        </div>
    );
};
